import { TrashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { showChooseLocationModal } from '../sharepoint/showChooseLocationModal';
import { showTypedConfirmModal } from '../ui/Modal';
import { useToast } from '../ui/toast';
import { isSharePointConfigured } from '../../config/sharepoint';
import { describeLocation } from '../../data/graph';
import { deleteTemplate, updateTemplate } from '../../data/templates';
import type { CategorySummary, SharePointLocation, TemplateSummary } from '../../data/types';

interface TemplateMetadataFormProps {
  template: TemplateSummary;
  categories: CategorySummary[];
  /** The team-site option only means something when the template has a Company field. */
  hasCompanyField: boolean;
  canDelete: boolean;
  onSaved: () => void;
}

export function TemplateMetadataForm({ template, categories, hasCompanyField, canDelete, onSaved }: TemplateMetadataFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description);
  const [categoryId, setCategoryId] = useState(template.categoryId);
  const [isPublished, setIsPublished] = useState(template.isPublished);
  const [allowSlideLibrary, setAllowSlideLibrary] = useState(template.allowSlideLibrary);
  const [allCountries, setAllCountries] = useState(template.allCountries);
  const [allLanguages, setAllLanguages] = useState(template.allLanguages);
  const [sharePointLocation, setSharePointLocation] = useState<SharePointLocation | null>(template.sharePointLocation ?? null);
  const [saveToCompanyTeamSite, setSaveToCompanyTeamSite] = useState(template.saveToCompanyTeamSite ?? false);
  const [busy, setBusy] = useState(false);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    try {
      await updateTemplate(template.id, {
        name: name.trim(),
        description: description.trim(),
        categoryId,
        isPublished,
        allowSlideLibrary,
        allCountries,
        allLanguages,
        saveToCompanyTeamSite,
        ...(sharePointLocation
          ? { sharePointLocation }
          : template.sharePointLocation
            ? { clearSharePointLocation: true }
            : {})
      });
      toast({ title: t('studio.detail.saved'), type: 'success' });
      onSaved();
    } catch (error) {
      toast({ title: error instanceof Error ? error.message : t('errors.saveTemplate'), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const confirmed = await showTypedConfirmModal({
      title: t('studio.detail.deleteTitle'),
      description: t('studio.detail.deleteDescription', { name: template.name }),
      confirmationText: template.name,
      label: t('studio.detail.deleteLabel'),
      confirmLabel: t('studio.detail.deleteConfirm')
    });
    if (!confirmed) return;

    try {
      await deleteTemplate(template.id);
      toast({ title: t('studio.detail.deleted'), type: 'success' });
      void navigate('/studio/templates');
    } catch (error) {
      toast({ title: error instanceof Error ? error.message : t('errors.deleteTemplate'), type: 'error' });
    }
  };

  const field =
    'mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

  return (
    <form onSubmit={save} className="mt-6 rounded-lg border border-border bg-surface p-4">
      <h2 className="text-base font-semibold text-foreground">{t('studio.detail.metadata')}</h2>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-foreground">{t('studio.upload.name')}</span>
          <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className={field} />
        </label>

        <label className="block text-sm">
          <span className="font-medium text-foreground">{t('studio.upload.category')}</span>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={field}>
            <option value="">{t('studio.upload.noCategory')}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm sm:col-span-2">
          <span className="font-medium text-foreground">{t('studio.upload.description')}</span>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className={field} />
        </label>
      </div>

      {isSharePointConfigured ? (
        <div className="mt-4 text-sm">
          <span className="font-medium text-foreground">{t('studio.detail.sharePointLocation')}</span>
          <p className="mt-1 text-xs text-muted-foreground">{t('studio.detail.sharePointLocationHint')}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="min-w-0 truncate text-foreground">
              {sharePointLocation
                ? describeLocation(sharePointLocation)
                : t('studio.detail.sharePointLocationNone')}
            </span>
            <button
              type="button"
              onClick={() => showChooseLocationModal(sharePointLocation, setSharePointLocation)}
              className="inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-1.5 text-sm font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {t('studio.detail.sharePointLocationChoose')}
            </button>
            {sharePointLocation ? (
              <button
                type="button"
                onClick={() => setSharePointLocation(null)}
                className="inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-1.5 text-sm font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                {t('common.remove')}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {isSharePointConfigured && (hasCompanyField || saveToCompanyTeamSite) ? (
        <label className="mt-4 block text-sm">
          <span className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={saveToCompanyTeamSite}
              onChange={(e) => setSaveToCompanyTeamSite(e.target.checked)}
              className="size-4 rounded border-input"
            />
            <span className="font-medium text-foreground">{t('studio.detail.saveToCompanyTeamSite')}</span>
          </span>
          <span className="mt-1 block text-xs text-muted-foreground">{t('studio.detail.saveToCompanyTeamSiteHint')}</span>
        </label>
      ) : null}

      <label className="mt-4 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isPublished}
          onChange={(e) => setIsPublished(e.target.checked)}
          className="size-4 rounded border-input"
        />
        <span className="font-medium text-foreground">{t('studio.detail.publish')}</span>
      </label>

      {template.documentType === 'pptx' ? (
        <label className="mt-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={allowSlideLibrary}
            onChange={(e) => setAllowSlideLibrary(e.target.checked)}
            className="size-4 rounded border-input"
          />
          <span className="font-medium text-foreground">{t('studio.detail.allowSlideLibrary')}</span>
        </label>
      ) : null}

      <label className="mt-2 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={allCountries}
          onChange={(e) => setAllCountries(e.target.checked)}
          className="size-4 rounded border-input"
        />
        <span className="font-medium text-foreground">{t('studio.detail.allCountries')}</span>
      </label>

      <label className="mt-2 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={allLanguages}
          onChange={(e) => setAllLanguages(e.target.checked)}
          className="size-4 rounded border-input"
        />
        <span className="font-medium text-foreground">{t('studio.detail.allLanguages')}</span>
      </label>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? t('studio.detail.saving') : t('studio.detail.save')}
        </button>

        {canDelete ? (
          <button
            type="button"
            onClick={() => void remove()}
            className="inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-2 text-sm font-semibold text-destructive transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-destructive"
          >
            <TrashIcon className="size-4" aria-hidden="true" />
            {t('studio.detail.delete')}
          </button>
        ) : null}
      </div>
    </form>
  );
}
